import { useCallback, useMemo, useState } from 'react';

import PrecisionService from '@core/services/PrecisionService';
import {
  useGetPolicyFleetCertificates,
  useGetPolicyFleetMarineCertificates,
  useGetPolicyGroupMedicalCertificates
} from '@features/production/policy/policy.slice';
import { capitalizeFirstCharLowerOthers } from '@utils/formatting-utils';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import {
  GetPolicyFleetCertificatesResponse,
  GetPolicyFleetCertificatesVariables,
  GetPolicyFleetMarineCertificatesResponse,
  GetPolicyFleetMarineCertificatesVariables,
  GetPolicyGroupMedicalCertificatesResponse,
  GetPolicyGroupMedicalCertificatesVariables
} from '../types';

import { GenericLines } from 'src/enums';

export type UseGetCertificatesOptions = {
  statuses?: string[] | null;
  keyByField?: string;
};

export function useGetCertificates(
  lineCode: string,
  policyID: string,
  defaultSorting: {
    pageNumber: number;
    searchKey: string;
    pageSize: number;
    order: 'asc' | 'desc';
    orderBy: string;
  },
  options?: UseGetCertificatesOptions
) {
  const lineCategory = getLineCategory(lineCode);

  const isGroupMedical =
    getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;

  const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;

  const isFleetLine = [4, 51].includes(Number(lineCode));

  const [certificatesKey, setCertificatesKey] = useState<number>(0);

  const [getGroupMedicalCertificates] = useGetPolicyGroupMedicalCertificates({
    skip: !isGroupMedical
  });

  const [getFleetCertificates] = useGetPolicyFleetCertificates({
    skip: !isFleetLine
  });

  const [getFleetMarineCertificates] = useGetPolicyFleetMarineCertificates({
    skip: !isMarine
  });

  const handlers = useMemo(
    () => ({
      groupMedical: {
        enabled: isGroupMedical,
        fetcher: getGroupMedicalCertificates,
        buildVariables: (
          baseVars: any
        ): GetPolicyGroupMedicalCertificatesVariables => ({
          policyID,
          ...baseVars,
          ...(options?.statuses !== undefined && { statuses: options.statuses })
        }),
        extractResponse: (r: {
          data: GetPolicyGroupMedicalCertificatesResponse;
        }) => r?.data?.production?.getPolicyCertificatePaginated
      },

      fleet: {
        enabled: isFleetLine,
        fetcher: getFleetCertificates as any,
        buildVariables: (
          baseVars: any
        ): GetPolicyFleetCertificatesVariables => ({
          policyID,
          ...baseVars
        }),
        extractResponse: (r: { data: GetPolicyFleetCertificatesResponse }) =>
          r?.data?.production?.groupFleetCertificatesForListing
      },
      marine: {
        enabled: isMarine,
        fetcher: getFleetMarineCertificates as any,
        buildVariables: (
          baseVars: any
        ): GetPolicyFleetMarineCertificatesVariables => ({
          policyID,
          ...baseVars
        }),
        extractResponse: (r: {
          data: GetPolicyFleetMarineCertificatesResponse;
        }) => r?.data?.production?.groupFleetMarineCertificatesForListing
      }
    }),
    [
      lineCategory,
      policyID,
      isGroupMedical,
      isFleetLine,
      getGroupMedicalCertificates,
      getFleetCertificates,
      options?.statuses
    ]
  );

  const mapItems = (items: any[]) =>
    isGroupMedical
      ? (items?.map((item: any) => ({
          ...item,
          coNSSF: capitalizeFirstCharLowerOthers(item.coNSSF)
        })) ?? [])
      : isFleetLine
        ? (items?.map((item: any) => ({
            ...item,
            policyId: item?.policy?.id, //for redirect to certificate policy details page
            certificateNumber: item?.policy?.policyNumber,
            subline: `${item.policy?.subline?.code} - ${item.policy?.subline?.name}`,
            plan: `${item.policy?.plan?.code} - ${item.policy?.plan?.name}`,
            makeModel: `${item.vehicle?.make?.title} - ${item.vehicle?.model?.title}`,
            yearOfMake: item?.vehicle?.yearOfMake,
            chassis: item?.vehicle?.chassis,
            effectiveDate: item?.policy?.policyEffectiveDate,
            carValue: item?.vehicle?.carValue,
            agencyYears: item?.policyMotor?.agencyRepairYears,
            depreciationYears: item?.policyMotor?.depreciationYears,
            sumInsured: item?.quote?.sumInsured,
            netPremium: item?.quote?.netPremium,
            charges: item?.quote?.chargesAmount,
            tpaFees: item?.quote?.tpaFeesTotalAmount,
            grossPremium: item?.quote?.grossPremium,
            proportionalStamp: item?.quote?.proportionalStampAmount,
            municipalityTax: item?.quote?.municipalityTaxAmount,
            totalPremium: item?.quote?.totalPremium
          })) ?? [])
        : isMarine
          ? (items?.map((item: any) => ({
              ...item,
              policyId: item?.policy?.id, //for redirect to certificate policy details page
              certificateNumber: item?.policy?.policyNumber,
              subline: `${item.policy?.subline?.code} - ${item.policy?.subline?.name}`,
              plan: `${item.policy?.plan?.code} - ${item.policy?.plan?.name}`,
              yearOfMake: item?.vehicle?.yearOfMake,
              effectiveDate: item?.policy?.policyEffectiveDate,
              sumInsured: item?.quote?.sumInsured,
              netPremium: item?.quote?.netPremium,
              charges: item?.quote?.chargesAmount,
              tpaFees: item?.quote?.tpaFeesTotalAmount,
              grossPremium: item?.quote?.grossPremium,
              proportionalStamp: item?.quote?.proportionalStampAmount,
              sumPerShipment: item.policyMarine?.sumPerShipment,
              sumPerShipmentCurrency:
                item.policyMarine?.sumPerShipmentCurrency?.symbol,
              municipalityTax: item?.quote?.municipalityTaxAmount,
              totalPremium: item?.quote?.totalPremium,
              matter: item.policyMarine?.matter.title,
              vessalName: item?.policyMarine?.vessalName,
              departure: item?.policyMarine?.departureFrom,
              destination: item?.policyMarine?.departureTo,
              transportationVia: item?.policyMarine?.transportationVia,
              survey: item?.policyMarine?.survey,
              loading: PrecisionService.multiplyBy100(
                item?.policyMarine?.loading
              )
            })) ?? [])
          : [];

  const fetchData = useCallback(
    async (
      pageNumber = defaultSorting.pageNumber,
      keyword = defaultSorting.searchKey,
      pageSize = defaultSorting.pageSize,
      order: 'asc' | 'desc' = defaultSorting.order,
      orderBy: string = defaultSorting.orderBy
    ) => {
      const baseVariables = {
        keyword,
        pagination: { pageNumber, pageSize },
        orderBy: {
          attribute: orderBy || defaultSorting.orderBy,
          descending: order === 'desc'
        }
      };

      const handler = Object.values(handlers).find((h) => h.enabled);

      if (!handler)
        return {
          pagedItems: [],
          pageNumber,
          pageSize,
          totalCount: 0
        };

      const response = await handler.fetcher({
        variables: handler.buildVariables(baseVariables)
      });

      const data = handler.extractResponse(response);
      const rawItems = data?.items ?? [];
      const items = mapItems(rawItems);

      const pagedItems = options?.keyByField
        ? items.reduce<Record<string, (typeof items)[number]>>((acc, item) => {
            const key = item[options.keyByField as keyof typeof item];
            if (key != null && key !== '') {
              acc[String(key)] = item;
            }

            return acc;
          }, {})
        : items;

      const paging = {
        pageNumber: data?.paging?.pageNumber ?? pageNumber,
        pageSize: data?.paging?.pageSize ?? pageSize,
        totalCount: data?.paging?.totalCount ?? items.length
      };

      return {
        pagedItems,
        ...paging
      };
    },
    [handlers, defaultSorting, isGroupMedical, options?.keyByField]
  );

  return { fetchData, certificatesKey, setCertificatesKey };
}
